import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Mock credentials for different user types - Updated to clearequal.com domain
  const mockCredentials = {
    'admin@clearequal.com': { password: 'Admin123!', role: 'admin', name: 'Admin User' },
    'manager@clearequal.com': { password: 'Manager123!', role: 'manager', name: 'Manager User' },
    'employee@clearequal.com': { password: 'Employee123!', role: 'employee', name: 'Employee User' },
    'auditor@clearequal.com': { password: 'Auditor123!', role: 'auditor', name: 'Auditor User' },
    'approver@clearequal.com': { password: 'Approver123!', role: 'approver', name: 'Approver User' }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = t('auth.errorEmailRequired');
    } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = t('auth.errorEmailInvalid');
    }
    
    if (!formData?.password) {
      newErrors.password = t('auth.errorPasswordRequired');
    } else if (formData?.password?.length < 6) {
      newErrors.password = t('auth.errorPasswordShort');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    // Simulate API call
    setTimeout(() => {
      const user = mockCredentials?.[formData?.email?.toLowerCase()];
      
      if (user && user?.password === formData?.password) {
        // Store user session
        localStorage.setItem('user', JSON.stringify({
          email: formData?.email,
          role: user?.role,
          name: user?.name,
          loginTime: new Date()?.toISOString()
        }));
        
        if (formData?.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }
        
        // Navigate based on role
        switch (user?.role) {
          case 'admin': navigate('/analytics-dashboard');
            break;
          case 'manager': navigate('/analytics-dashboard');
            break;
          case 'approver': navigate('/approvals');
            break;
          case 'employee': navigate('/employee-portal');
            break;
          case 'auditor': navigate('/compliance-reports');
            break;
          default:
            navigate('/analytics-dashboard');
        }
      } else {
        setErrors({ 
          general: t('auth.errorInvalidCredentials') 
        });
      }
      
      setIsLoading(false);
    }, 1500);
  };

  const handleForgotPassword = () => {
    // Mock forgot password functionality
    alert(t('auth.forgotPasswordModal'));
  };

  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors?.general && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-red-600" />
              <p className="text-sm text-red-700">{errors?.general}</p>
            </div>
          </div>
        )}

        <Input
          label={t('auth.emailLabel')}
          type="email"
          name="email"
          placeholder={t('auth.emailPlaceholder')}
          value={formData?.email}
          onChange={handleInputChange}
          error={errors?.email}
          required
          disabled={isLoading}
        />

        <div className="relative">
          <Input
            label={t('auth.passwordLabel')}
            type={showPassword ? 'text' : 'password'}
            name="password"
            placeholder={t('auth.passwordPlaceholder')}
            value={formData?.password}
            onChange={handleInputChange}
            error={errors?.password}
            required
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
            disabled={isLoading}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            label={t('auth.rememberMe')}
            name="rememberMe"
            checked={formData?.rememberMe}
            onChange={handleInputChange}
            disabled={isLoading}
          />
          
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm text-accent hover:text-accent/80 transition-smooth font-medium"
            disabled={isLoading}
          >
            {t('auth.forgotPassword')}
          </button>
        </div>

        <Button
          type="submit"
          variant="gold"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
        >
          {t('auth.signIn')}
        </Button>
      </form>

    </div>
  );
};

export default LoginForm;